import { SaleChannel } from '@/constants'
import { useAuth } from '@/containers'
import type { Property, PropertyValue } from '@/types'
import { getTranslate } from '@/utils/api-interceptors'
import { useIsomorphicLayoutEffect } from '@/utils/useIsomorphicLayoutEffect'
import { Disclosure, Transition } from '@headlessui/react'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { useRef, useState, type ReactNode } from 'react'

interface SpecificationsType {
  specifications: Property[]
  specificationTitle: string
}
const renderSpecificationsItem = (item: Property): ReactNode => {
  switch (item.type as string) {
    case 'multipleSelect':
      return item.values.map((e: PropertyValue, i: number) => {
        return (
          <span key={i}>
            {getTranslate(e.value)}
            {i < item.values.length - 1 && ', '}
          </span>
        )
      })
    case 'singleSelect':
      return item.values.map((e: PropertyValue, i: number) => {
        return e.link ? (
          <Link key={i} href={e.link || ''} target='_blank'>
            <span className="mr-1 inline-block cursor-pointer text-primary-base after:content-[','] last:mr-0 last:after:content-[unset]">
              {getTranslate(e.value)}
            </span>
          </Link>
        ) : (
          <span
            className="mr-1 inline-block after:content-[','] last:mr-0 last:after:content-[unset]"
            key={i}
          >
            {getTranslate(e.value)}
          </span>
        )
      })
    case 'input':
      return (
        <div
          className='whitespace-pre-line'
          dangerouslySetInnerHTML={{
            __html: getTranslate(item.value),
          }}
        ></div>
      )
    default:
      break
  }
}
export const Specifications = ({
  specifications,
  specificationTitle,
}: SpecificationsType) => {
  const t = useTranslations('ProductDetail')
  const targetRef = useRef() as React.MutableRefObject<HTMLUListElement>
  const [dimensions, setDimensions] = useState({ height: 0 })

  useIsomorphicLayoutEffect(() => {
    if (targetRef.current) {
      setDimensions({
        height: targetRef.current.offsetHeight,
      })
    }
  }, [])
  const [isReadMoreSpec, setIsReadMoreSpec] = useState(true)

  const toggleReadMore = () => {
    setIsReadMoreSpec(!isReadMoreSpec)
  }
  const { token, user } = useAuth()
  const specificationsData = specifications?.filter(
    item => !item?.policy && item?.channels?.includes(SaleChannel.B2C)
  )
  const specificationsDataB2E = specifications?.filter(
    item => !item?.policy && item?.channels?.includes(SaleChannel.B2E)
  )
  return (
    <>
      {specifications && specifications.length > 0 && (
        <div className='information w-full'>
          <h2 className='mb-3 pt-5 text-left text-lg font-bold text-dark-400'>
            {specificationTitle}
          </h2>
          <Disclosure defaultOpen={true} as='div' className='flex w-full'>
            {({ open }) => (
              <div className='relative w-full'>
                <Transition
                  show={open}
                  enter='transition duration-1000 ease-out'
                  enterFrom='transform scale-95 opacity-0'
                  enterTo='transform scale-100 opacity-100'
                  leave='transition duration-75 ease-out'
                  leaveFrom='transform scale-100 opacity-100'
                  leaveTo='transform scale-95 opacity-0'
                >
                  <div
                    className={` list-specifications overflow-hidden text-dark-400 ${
                      !isReadMoreSpec ? 'max-h-full' : 'max-h-[400px]'
                    }`}
                  >
                    {token && user?.saleChannel === SaleChannel.B2E ? (
                      <ul
                        className='relative flex w-full flex-wrap'
                        ref={targetRef}
                      >
                        {specificationsDataB2E &&
                          specificationsDataB2E.map((item, index) => {
                            if (
                              getTranslate(item.value) != '' ||
                              item.values.length > 0
                            ) {
                              return (
                                <li
                                  key={index}
                                  className='relative flex w-full flex-wrap border-t-[1px] border-dark-100 py-2 last:border-b-[1px]'
                                >
                                  <span className='label w-full max-w-[45%] flex-[45%] pr-3 text-base font-medium'>
                                    {getTranslate(item.name)}
                                  </span>
                                  <span className='max-w-[55%] flex-[55%]'>
                                    <span className='value text-base'>
                                      {renderSpecificationsItem(item)}
                                    </span>
                                  </span>
                                </li>
                              )
                            }
                          })}
                      </ul>
                    ) : (
                      <ul
                        className='relative flex w-full flex-wrap'
                        ref={targetRef}
                      >
                        {specificationsData &&
                          specificationsData.map((item, index) => {
                            if (
                              getTranslate(item.value) != '' ||
                              item.values.length > 0
                            ) {
                              return (
                                <li
                                  key={index}
                                  className={`${
                                    index % 2 == 0 ? 'bg-white-500' : ''
                                  } relative flex w-full flex-wrap border-t-[1px] border-dark-100 px-2 py-2 last:border-b-[1px]`}
                                >
                                  <span className='label w-full max-w-[45%] flex-[45%] pr-3 text-base font-medium'>
                                    {getTranslate(item.name)}
                                  </span>
                                  <span className='max-w-[55%] flex-[55%]'>
                                    <span className='value text-base'>
                                      {renderSpecificationsItem(item)}
                                    </span>
                                  </span>
                                </li>
                              )
                            }
                          })}
                      </ul>
                    )}
                  </div>
                </Transition>
                {isReadMoreSpec && dimensions.height > 400 && (
                  <div className='absolute bottom-0 left-0 h-[100px] w-full bg-seeMore bg-bottom'></div>
                )}
              </div>
            )}
          </Disclosure>
          {dimensions.height > 400 ? (
            <div
              className={`relative z-50 flex w-full justify-center ${
                isReadMoreSpec ? '-mt-5' : 'mt-1'
              }`}
            >
              <button
                onClick={toggleReadMore}
                className={`mx-auto flex w-full items-center justify-center gap-x-1 rounded border border-blue-base bg-white px-5 py-2 text-center font-semibold leading-6 text-blue-base hover:bg-blue-base hover:text-white`}
              >
                {isReadMoreSpec ? `${t('readmore')}` : `${t('collapse')}`}
              </button>
            </div>
          ) : (
            ''
          )}
        </div>
      )}
    </>
  )
}

export default Specifications
